import { computed } from 'vue'

export default () => {
  const FEATURE_FLAGS = {
    DRIP_CAMPAIGNS: 'drip_campaigns',
    CONTACT_COLLISIONS: 'contact_collisions',
    NEW_STATS: 'new_stats',
    TWILIO_AUTOCOMPLETE_CALLERNAME: 'twilio_autocomplete_callername',
    FORCE_EASTERN_TZ: 'force_eastern_tz',
    EMAIL_NOTIFICATIONS: 'email_notifications',
    CAMPAIGN_EXPORT: 'campaign_export',
    INTEGRATIONS_GUI: 'integrations_gui',
    SEAT_MANAGEMENT: 'seat_management',
    NYLAS_INTEGRATION: 'nylas_integration',
    MEETING_NOTETAKER: 'nylas_notetaker',
    ACCOUNT_TEMPLATES: 'account_templates',
    ACTIVITY_OVERVIEW_SEAT_SELECTION: 'activity_overview_seat_selection',
    DISABLE_TRANSLATIONS: 'disable_translations',
    ENFORCE_OPT_OUT: 'ENFORCE_OPT_OUT',
    DISABLE_PASSWORD: 'disable_password',
    VOICE_CALL: 'voice_call',
    SHOW_VOICE_CALLS: 'show_voice_calls',
    ENABLE_EMOJIS: 'enable_emojis',
    VALIDATE_REASSIGNED_PHONES: 'validate_reassigned_phones',
    DELIVERY_STATUS: 'delivery_status',
    OUT_OF_OFFICE: 'out_of_office',
    VOICE_CALL_TRANSCRIPTIONS: 'voice_call_transcriptions',
    AI_NAME_RECOGNITION: 'ai_name_recognition',
    SEND_MMS: 'send_mms',
    CAMPAIGNS_MERGED_UI: 'campaigns_merged_ui',
    INTEGRATIONS_ADVANCED_SEARCH: 'hcm_integration',
    INTERCOM_CHATBOT: 'intercom_chatbot',
    STRIPE_BILLING: 'stripe_billing'
  }

  const user = useUserStore()
  const currentTenantFeatureFlags = computed(() => user?.currentTenantInfo?.feature_flags || [])

  const hasFeatureFlag = (flag: string) => currentTenantFeatureFlags.value.includes(flag)

  const hasAccessToDripCampaigns = computed(() => hasFeatureFlag(FEATURE_FLAGS.DRIP_CAMPAIGNS))
  const enableContactCollisions = computed(() => hasFeatureFlag(FEATURE_FLAGS.CONTACT_COLLISIONS))
  const hasAccessToNewStats = computed(() => hasFeatureFlag(FEATURE_FLAGS.NEW_STATS))
  const hasTwilioAutocompleteCallername = computed(() => hasFeatureFlag(FEATURE_FLAGS.TWILIO_AUTOCOMPLETE_CALLERNAME))
  const canSelectAllActivityOverviewSeats = computed(() => !!hasFeatureFlag(FEATURE_FLAGS.ACTIVITY_OVERVIEW_SEAT_SELECTION))
  const canUseTranslations = computed(() => !hasFeatureFlag(FEATURE_FLAGS.DISABLE_TRANSLATIONS))
  const enforceAddOptOutMessage = computed(() => !!hasFeatureFlag(FEATURE_FLAGS.ENFORCE_OPT_OUT))
  const canResetPassword = computed(() => !hasFeatureFlag(FEATURE_FLAGS.DISABLE_PASSWORD))
  const canUseVoiceCalls = computed(() => !!hasFeatureFlag(FEATURE_FLAGS.VOICE_CALL))
  const canUseEmojis = computed(() => !!hasFeatureFlag(FEATURE_FLAGS.ENABLE_EMOJIS))
  const hasToForceEasternTz = computed(() => !!hasFeatureFlag(FEATURE_FLAGS.FORCE_EASTERN_TZ))
  const hasToValidateReassignedPhones = computed(() => hasFeatureFlag(FEATURE_FLAGS.VALIDATE_REASSIGNED_PHONES))
  const showVoiceCallsPreview = computed(() => hasFeatureFlag(FEATURE_FLAGS.SHOW_VOICE_CALLS))
  const hasOOOPermission = computed(() => hasFeatureFlag(FEATURE_FLAGS.OUT_OF_OFFICE))
  const hasEmailNotificationsPermission = computed(() => hasFeatureFlag(FEATURE_FLAGS.EMAIL_NOTIFICATIONS))
  const hasAccessToCampaignExport = computed(() => hasFeatureFlag(FEATURE_FLAGS.CAMPAIGN_EXPORT))
  const hasSharedTemplatesFF = computed(() => hasFeatureFlag(FEATURE_FLAGS.ACCOUNT_TEMPLATES))
  const hasSeatManagementFF = computed(() => hasFeatureFlag(FEATURE_FLAGS.SEAT_MANAGEMENT))
  const canShowDeliveryStatus = computed(() => hasFeatureFlag(FEATURE_FLAGS.DELIVERY_STATUS))
  const canSeeTranscriptions = computed(() => hasFeatureFlag(FEATURE_FLAGS.VOICE_CALL_TRANSCRIPTIONS))
  const hasAccessToIntegrations = computed(() => hasFeatureFlag(FEATURE_FLAGS.INTEGRATIONS_GUI))
  const hasCalendarAccess = computed(() => hasFeatureFlag(FEATURE_FLAGS.NYLAS_INTEGRATION))
  const hasMeetingNotetaker = computed(() => hasFeatureFlag(FEATURE_FLAGS.MEETING_NOTETAKER))
  const hasAINameRecognition = computed(() => hasFeatureFlag(FEATURE_FLAGS.AI_NAME_RECOGNITION))
  const canSendMMS = computed(() => hasFeatureFlag(FEATURE_FLAGS.SEND_MMS))
  const showCampaignsMergedUI = computed(() => hasFeatureFlag(FEATURE_FLAGS.CAMPAIGNS_MERGED_UI))
  const hasIntegrationsAdvancedSearch = computed(() => hasFeatureFlag(FEATURE_FLAGS.INTEGRATIONS_ADVANCED_SEARCH))
  const hasIntercomChatbot = computed(() => hasFeatureFlag(FEATURE_FLAGS.INTERCOM_CHATBOT))
  const hasBillingAccess = computed(() =>
    hasFeatureFlag(FEATURE_FLAGS.STRIPE_BILLING) && user.readable?.isAdminOrOwner
  )

  return {
    FEATURE_FLAGS,
    hasAccessToDripCampaigns,
    enableContactCollisions,
    hasAccessToNewStats,
    hasTwilioAutocompleteCallername,
    canSelectAllActivityOverviewSeats,
    canUseTranslations,
    enforceAddOptOutMessage,
    canResetPassword,
    canUseVoiceCalls,
    canUseEmojis,
    hasToForceEasternTz,
    hasToValidateReassignedPhones,
    showVoiceCallsPreview,
    hasOOOPermission,
    hasEmailNotificationsPermission,
    hasAccessToCampaignExport,
    hasSharedTemplatesFF,
    hasSeatManagementFF,
    canShowDeliveryStatus,
    canSeeTranscriptions,
    hasAccessToIntegrations,
    hasCalendarAccess,
    hasMeetingNotetaker,
    hasAINameRecognition,
    canSendMMS,
    showCampaignsMergedUI,
    hasIntegrationsAdvancedSearch,
    hasIntercomChatbot,
    hasBillingAccess
  }
}
